import React, { FC, useCallback, useEffect, useState, EffectCallback } from 'react'
import get from 'lodash/fp/get'
import { useAppDispatch } from '../../../hooks/redux'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button, LottieLoader, Notification } from '@mondra/ui-components'
import { useAppSelector } from '../../../hooks/redux'
import { useFormik } from 'formik'
import { fetchGeneric, getConfiguration } from './genericsActions'
import { showCorrelationIdError } from '../../../redux/correlationIdSlice'
import { transformFormValues } from '../../../shared/formFunctions'
import { TAB_LABELS, TAB_ERRORS, TabsEnum } from './utils'
import { formSchema } from './genericFormSchema'
import { updateGenerics, publishGenerics, unPublishGenerics, createGenerics } from './api'

import { usePrompt } from '../../../hooks/usePrompt'
import { handlePublishStatus, setDefaultGeneric } from '../../../redux/genericSlice'

import { ContentTabs } from '../../../components/ContentTabs'
import { RecordHeader } from '../../../components/RecordHeader'
import { GenericProductInfo } from './GenericProductInfo'
import { GenericImpacts } from './GenericImpacts'
import { GenericEmissions } from './GenericEmissions'
import { GenericDataQualityScore } from './GenericDataQualityScore'
import './index.scss'
import ActivityRightPanel from '../../../components/ActivityRightPanel'
import { ActivityTypes, ROUTES } from '../../../const'
import { IGpcBrick, Dropdown } from './../../../components/AttributeSelector/types'
import {
  getProcessedDropdowns,
  getProduceDropdowns,
  getProduct,
  getProductDropdowns,
  getSpecialIngredientDropdowns,
} from '../../../shared/taxonomyApi'

const Generic: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  let [searchParams] = useSearchParams()
  const { loading, generic, configurationLoading, configuration } = useAppSelector(
    state => state.genericSlice
  )
  const { loading: companiesLoading } = useAppSelector(state => state.companiesSlice)
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [showError, setShowError] = useState<string>('')
  const [errors, setErrors] = useState<[any, string[]][]>()
  const [showPublished, setShowPublished] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const [brick, setBrick] = useState<IGpcBrick>()
  const [categoryOptions, setCategoryOptions] = useState<[] | Dropdown[]>([])
  const [groupOptions, setGroupOptions] = useState<[] | Dropdown[]>([])
  const [functionalUnit, setFunctionalUnit] = useState<[] | Dropdown[]>([])

  const genericId = searchParams.get('itemId') || ''
  const isNew = genericId === 'new'

  const navigateToTable = useCallback(() => {
    navigate(ROUTES.generics)
  }, [navigate])

  useEffect((): ReturnType<EffectCallback> => {
    if (!companiesLoading) {
      if (genericId === '') {
        navigate('/not-found')
      }

      dispatch(getConfiguration())

      if (!isNew) {
        dispatch(fetchGeneric(genericId, () => navigateToTable()))
      }

      return () => {
        dispatch(setDefaultGeneric())
      }
    }
  }, [companiesLoading, isNew, genericId, dispatch, navigate, navigateToTable])

  const formik = useFormik({
    initialValues: {
      ...generic,
    },
    validationSchema: formSchema,
    onSubmit: async () => {
      if (generic.state === 0) {
        publishGenerics(genericId)
          .then(() => {
            dispatch(handlePublishStatus(1))
            setShowPublished(true)
            setTimeout(() => {
              setShowPublished(false)
            }, 4000)
          })
          .catch(error => {
            const messages = error.response.data[Object.keys(error.response.data)[0]]

            setShowError(messages?.errorMessage || messages[0])
          })
      } else {
        unPublishGenerics(genericId)
          .then(() => {
            dispatch(handlePublishStatus(0))
            setShowPublished(true)
            setTimeout(() => {
              setShowPublished(false)
            }, 4000)
          })
          .catch(error => setShowError(error.response.data.title))
      }
    },
  })

  useEffect(() => {
    formik.setValues({ ...generic })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generic])

  const getTabErrors = (tab: TabsEnum) => {
    return TAB_ERRORS[tab].some(value => get(value, formik.touched) && !!get(value, formik.errors))
  }

  const tabs = [
    {
      label: TAB_LABELS[TabsEnum.PRODUCT],
      hasErrors: getTabErrors(TabsEnum.PRODUCT),
    },
    {
      label: TAB_LABELS[TabsEnum.EMISSIONS],
      hasErrors: getTabErrors(TabsEnum.EMISSIONS),
    },
    {
      label: TAB_LABELS[TabsEnum.IMPACTS],
      hasErrors: getTabErrors(TabsEnum.IMPACTS),
    },
    {
      label: TAB_LABELS[TabsEnum.DQS],
      hasErrors: getTabErrors(TabsEnum.DQS),
    },
  ]

  const saveDraft = async e => {
    e.preventDefault()
    if (formik.isSubmitting) {
      return
    }
    formik.setSubmitting(true)

    const { values } = formik
    setErrors([])
    setShowError('')

    const tValues = transformFormValues(values)

    if (isNew) {
      try {
        await createGenerics(tValues).then(() => {
          navigateToTable()
        })
      } catch (error: any) {
        if (error.showCorrelationId) {
          dispatch(showCorrelationIdError(error.CorrelationId))
        } else {
          setShowError(
            typeof error.response.data === 'string'
              ? error.response.data
              : typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.values(error.response.data.errors).toString().replace(/!,/g, '!\n')
          )
          setTimeout(() => {
            setShowError('')
          }, 5000)
          setErrors(
            typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.entries(error.response.data.errors)
          )
        }
      } finally {
        formik.setSubmitting(false)
      }
    } else {
      try {
        await updateGenerics(genericId, tValues)
          .then(() => {
            dispatch(fetchGeneric(genericId, () => navigateToTable()))
            dispatch(getConfiguration())
          })
          .then(() => {
            setShowNotification(true)
            setTimeout(() => {
              setShowNotification(false)
            }, 5000)
          })
      } catch (error: any) {
        if (error.showCorrelationId) {
          dispatch(showCorrelationIdError(error.CorrelationId))
        } else {
          setShowError(
            typeof error.response.data === 'string'
              ? error.response.data
              : typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.values(error.response.data.errors).toString().replace(/!,/g, '!\n')
          )
          setTimeout(() => {
            setShowError('')
          }, 5000)
          setErrors(
            typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.entries(error.response.data.errors)
          )
        }
      } finally {
        formik.setSubmitting(false)
      }
    }
  }

  usePrompt(
    'There are unsaved changes, do you want to leave?',
    !isNew ? JSON.stringify(generic) !== JSON.stringify(formik.values) : false
  )

  const fetchCategories = useCallback(async () => {
    const productType = formik.values.productType

    // Product categories
    if (productType === 1) {
      getProductDropdowns('segment').then(res => {
        if (res?.data[0]?.id) {
          getProductDropdowns(`family`).then(res => {
            setCategoryOptions(res.data)
          })
        }
      })
      return
    }
    // Produce and Processed Ingredient categories
    if (productType === 0 || productType === 2) {
      getProduceDropdowns('category?classId=1').then(res => setCategoryOptions(res.data))
      return
    }
    // Special Ingredient categories
    if (productType === 3) {
      getSpecialIngredientDropdowns('category?classId=1').then(res => setCategoryOptions(res.data))
      return
    }
  }, [formik.values.productType])

  const fetchGroups = useCallback(async () => {
    const productType = formik.values.productType
    const categoryId = formik.values.productCategoryId

    if (!categoryId) {
      return
    }

    // Product groups
    if (productType === 1) {
      getProductDropdowns(`class?familyId=${categoryId}`).then(res => {
        setGroupOptions(res.data)
      })
      return
    }
    // Produce and Processed Ingredient groups
    if (productType === 0 || productType === 2) {
      getProduceDropdowns(`subcategory?categoryId=${categoryId}`).then(res =>
        setGroupOptions(res.data)
      )
      return
    }
    // Special Ingredient groups
    if (productType === 3) {
      getSpecialIngredientDropdowns(`functionalUnit?categoryId=${categoryId}`).then(res =>
        setFunctionalUnit(res.data)
      )
      return
    }
  }, [formik.values.productType, formik.values.productCategoryId])

  const fetchFunctionalUnit = useCallback(async () => {
    const productType = formik.values.productType
    const groupId = formik.values.productGroupId

    if (!groupId) {
      return
    }

    // Product functional units
    if (productType === 1) {
      getProductDropdowns(`brick?classId=${groupId}`).then(res => {
        setFunctionalUnit(res.data)
      })
      return
    }
    // Produce functional units
    if (productType === 0) {
      getProduceDropdowns(`functionalUnit?subcategoryId=${groupId}`).then(res =>
        setFunctionalUnit(res.data)
      )
      return
    }

    // Processed ingredient functional units
    if (productType === 2) {
      getProcessedDropdowns(`functionalUnit?produceSubcategoryId=${groupId}`).then(res =>
        setFunctionalUnit(res.data)
      )
      return
    }
  }, [formik.values.productType, formik.values.productGroupId])

  const fetchBrick = useCallback(async () => {
    const productType = formik.values.productType
    const productId = formik.values.productId

    if (productType === 1) {
      getProduct(`${productId}`).then(res => setBrick(res.data))
    }
  }, [formik.values.productType, formik.values.productId])

  // Handle dependent dropdowns on product type change
  useEffect(() => {
    const productType = formik.values.productType

    setCategoryOptions([])
    setGroupOptions([])
    setFunctionalUnit([])

    if (productType !== null) {
      fetchCategories()
    }
  }, [formik.values.productType, fetchCategories])

  // Handle dependent dropdowns on category change
  useEffect(() => {
    const categoryId = formik.values.productCategoryId

    setGroupOptions([])
    setFunctionalUnit([])

    if (categoryId) {
      fetchGroups()
    }
  }, [formik.values.productCategoryId, fetchGroups])

  // Handle dependent dropdowns on group change
  useEffect(() => {
    const groupId = formik.values.productGroupId

    setFunctionalUnit([])

    if (groupId) {
      fetchFunctionalUnit()
    }
  }, [formik.values.productGroupId, fetchFunctionalUnit])

  // Handle brick data for AttributeSelector component
  useEffect(() => {
    const productId = formik.values.productId
    const productType = formik.values.productType

    setBrick(undefined)

    if (productId && productType === 1) {
      fetchBrick()
    }
  }, [formik.values.productId, formik.values.productType, fetchBrick])

  return (loading !== true && configurationLoading !== true) || isNew ? (
    <>
      <div className="piece content">
        {showError !== '' && (
          <div className={'piece-notification'}>
            <Notification
              type="error"
              label="Oops:"
              description={showError}
              onClose={() => setShowError('')}
            />
          </div>
        )}
        {showPublished && (
          <div className={'piece-notification'}>
            <Notification
              type="success"
              label="Success:"
              description="Status was updated!"
              isToast={false}
            />
          </div>
        )}
        {showNotification && (
          <div className={'piece-notification'}>
            <Notification
              type="success"
              label="Success:"
              description="Item was updated!"
              isToast={false}
            />
          </div>
        )}
        <div className="mb-5 mt-5">
          <Breadcrumbs
            routes={[
              {
                path: ROUTES.generics,
                title: 'Generics Table',
              },
              {
                path: `${ROUTES.generic}?itemId=${genericId}`,
                title: 'Generic Item',
              },
            ]}
          />
        </div>
        {!isNew && (
          <RecordHeader
            label={formik.values.name}
            isPublished={generic.state !== 0}
            datePublished={generic?.lastPublishedDate}
          />
        )}

        <ContentTabs
          items={tabs}
          selectedIndex={selectedTab}
          onChange={index => setSelectedTab(index)}
        />

        <form onSubmit={formik.handleSubmit} className="piece-content">
          {selectedTab === 0 && (
            <GenericProductInfo
              formik={formik}
              productTypes={configuration.productTypes}
              productionSystems={configuration.productionSystems}
              brick={brick}
              categoryOptions={categoryOptions}
              groupOptions={groupOptions}
              functionalUnit={functionalUnit}
            />
          )}
          {selectedTab === 1 && <GenericEmissions formik={formik} />}
          {selectedTab === 2 && <GenericImpacts formik={formik} />}
          {selectedTab === 3 && <GenericDataQualityScore formik={formik} />}
          {errors &&
            (Array.isArray(errors) ? (
              <ul className="piece-errors">
                {errors.map(([key, value]) =>
                  value.map(err => (
                    <li>
                      <b>{key}:</b> {err}
                    </li>
                  ))
                )}
              </ul>
            ) : (
              <ul className="piece-errors">
                <li>{errors}</li>
              </ul>
            ))}
          <div className="piece-actions">
            {sessionStorage.getItem('permission') === 'true' && genericId !== 'new' && (
              <Button
                variant="tertiary"
                size="md"
                type="submit"
                className="w-32"
                disabled={
                  formik.isSubmitting || JSON.stringify(generic) !== JSON.stringify(formik.values)
                }
              >
                {generic.state === 0 ? 'Publish' : 'Unpublish'}
              </Button>
            )}
            {JSON.stringify(generic) !== JSON.stringify(formik.values) && (
              <div
                className="text-color-rose-500 rounded border-l-4 border-orange-500 bg-orange-100 px-4 py-2"
                role="alert"
              >
                <p>Please save before publishing new changes</p>
              </div>
            )}
            <Button
              variant="primary"
              size="md"
              onClick={saveDraft}
              className="w-32"
              disabled={formik.isSubmitting}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={genericId}
        activityType={ActivityTypes.Generics}
        auditCreatedBy={generic.researcherName}
        auditLastUpdatedBy={generic.lastUpdatedName}
        companyId={null}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
      />
    </>
  ) : (
    <div className="overlay">
      <LottieLoader
        lottieType="butterflyLottie"
        style={{ height: '10.25rem', width: '10.25rem' }}
      />
    </div>
  )
}

export default Generic
